'use client';
import React from 'react';

type AnswerState = 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled';

interface AnswerCardProps {
  optionId: string;
  text: string;
  label: string;
  state: AnswerState;
  onSelect: (optionId: string) => void;
  disabled: boolean;
}

const stateConfig: Record<AnswerState, {
  containerClass: string;
  labelClass: string;
  textClass: string;
  icon: React.ReactNode | null;
}> = {
  default: {
    containerClass: 'answer-card-default hover:answer-card-hover cursor-pointer',
    labelClass: 'bg-secondary/80 text-secondary-foreground border-border-light',
    textClass: 'text-foreground',
    icon: null,
  },
  selected: {
    containerClass: 'cursor-pointer',
    labelClass: 'bg-primary/20 text-primary-light border-primary/40',
    textClass: 'text-foreground',
    icon: null,
  },
  correct: {
    containerClass: 'answer-card-correct',
    labelClass: 'bg-success/20 text-success-light border-success/40',
    textClass: 'text-success-light font-medium',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <circle cx="9" cy="9" r="8" fill="rgba(16,185,129,0.2)" stroke="#10B981" strokeWidth="1.5" />
        <path d="M5.5 9l2.5 2.5 4.5-4.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  incorrect: {
    containerClass: 'answer-card-incorrect',
    labelClass: 'bg-error/20 text-error-light border-error/40',
    textClass: 'text-error-light font-medium',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <circle cx="9" cy="9" r="8" fill="rgba(239,68,68,0.15)" stroke="#EF4444" strokeWidth="1.5" />
        <path d="M6 6l6 6M12 6l-6 6" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  disabled: {
    containerClass: 'answer-card-default answer-card-disabled',
    labelClass: 'bg-secondary/60 text-muted-foreground border-border',
    textClass: 'text-muted-foreground',
    icon: null,
  },
};

export default function AnswerCard({ optionId, text, label, state, onSelect, disabled }: AnswerCardProps) {
  const config = stateConfig[state];

  const handleClick = () => {
    if (!disabled && state === 'default') {
      onSelect(optionId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && state === 'default') {
      e.preventDefault();
      onSelect(optionId);
    }
  };

  const isSelected = state === 'selected';

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-pressed={isSelected}
      aria-disabled={disabled}
      aria-label={`Option ${label}: ${text}${state === 'correct' ? ' — Correct answer' : state === 'incorrect' ? ' — Incorrect answer' : ''}`}
      className={`
        relative rounded-xl p-4 sm:p-5 flex items-center gap-4 select-none
        transition-all duration-200
        ${config.containerClass}
        ${isSelected ? 'border-primary/60 bg-primary/10 shadow-glow-blue' : ''}
        focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2
      `}
      style={isSelected ? {
        background: 'rgba(59,130,246,0.1)',
        border: '1.5px solid rgba(59,130,246,0.5)',
        boxShadow: '0 0 20px rgba(59,130,246,0.12)',
      } : undefined}
    >
      {/* Option label */}
      <span
        className={`
          shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center
          font-semibold text-sm transition-all duration-200
          ${config.labelClass}
        `}
        aria-hidden="true"
      >
        {label}
      </span>

      {/* Option text */}
      <span className={`flex-1 text-sm sm:text-base leading-relaxed transition-colors duration-200 ${config.textClass}`}>
        {text}
      </span>

      {/* State icon */}
      {config.icon && (
        <span className="shrink-0 fade-in" aria-hidden="true">
          {config.icon}
        </span>
      )}
    </div>
  );
}